/** Field validation shared by the phone profile form, the employer grid and the API. */

export interface FieldError {
  field: string;
  expected: string;
}

const RE = {
  kennitala: /^\d{6}-?\d{4}$/,
  phone: /^[\d\s+]{7,12}$/,
  email: /^\S+@\S+\.\S+$/,
  bank: /^\d{4}-\d{2}-\d{6}$/,
  date: /^\d{4}-\d{2}-\d{2}$/,
};

export function normalizeKennitala(kt: string): string {
  return (kt || '').replace(/\D/g, '');
}

/** Icelandic kennitala check digit (modulus 11). */
export function isValidKennitala(kt: string): boolean {
  const d = normalizeKennitala(kt);
  if (d.length !== 10) return false;
  const weights = [3, 2, 7, 6, 5, 4, 3, 2];
  const sum = weights.reduce((a, w, i) => a + w * Number(d[i]), 0);
  const rem = sum % 11;
  const check = rem === 0 ? 0 : 11 - rem;
  return check === 10 ? false : check === Number(d[8]);
}

export function validateKennitala(kt: string): string | null {
  return RE.kennitala.test((kt || '').trim()) ? null : '000000-0000';
}

export function validatePhone(v: string): string | null {
  return RE.phone.test((v || '').trim()) ? null : '000 0000';
}

export function validateEmail(v: string): string | null {
  return RE.email.test((v || '').trim()) ? null : 'a@b.is';
}

export function validateBank(v: string): string | null {
  return RE.bank.test((v || '').trim()) ? null : '0000-26-000000';
}

export function validateDate(v: string): string | null {
  return RE.date.test((v || '').trim()) ? null : 'YYYY-MM-DD';
}

export function validatePercent(v: unknown, min = 0, max = 100): string | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) && n >= min && n <= max ? null : `${min}–${max}`;
}

export function validateNonNegative(v: unknown): string | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  return Number.isFinite(n) && n >= 0 ? null : '≥ 0';
}

/** Hours for a single day. */
export function validateHours(v: unknown): string | null {
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 && n <= 24 ? null : '0–24';
}

/** Validate the fields an employee may edit themselves. */
export function validateEmployeeSelfService(f: {
  phone?: string;
  email?: string;
  bank?: string;
  creditUtil?: unknown;
  priorUnusedCredit?: unknown;
}): Record<string, string> {
  const errs: Record<string, string> = {};
  const put = (k: string, e: string | null) => {
    if (e) errs[k] = e;
  };
  put('phone', validatePhone(f.phone ?? ''));
  put('email', validateEmail(f.email ?? ''));
  put('bank', validateBank(f.bank ?? ''));
  put('creditUtil', validatePercent(f.creditUtil));
  put('priorUnusedCredit', validateNonNegative(f.priorUnusedCredit));
  return errs;
}
